import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import hitIcon from '../../../../../assets/icons/hit.png';
import missIcon from '../../../../../assets/icons/miss.png';
import backIcon from '../../../../../assets/icons/back-arrow.png';
import { scoringService } from '../../../../../services';
import './DashboardScore.css';
import './ScoreShoot.css';

const parseScoreData = (scoreDataJson) => {
    try {
        return JSON.parse(scoreDataJson);
    } catch (error) {
        console.error('Failed to parse score data:', error);
        return { scores: [], pattern: [], totalShots: 0 };
    }
};

const parseNoBirdIndexes = (noBirdsJson) => {
    if (!noBirdsJson) {
        return new Set();
    }

    try {
        const parsed = JSON.parse(noBirdsJson);
        const indexes = Array.isArray(parsed)
            ? parsed
            : Array.isArray(parsed?.indexes)
                ? parsed.indexes
                : [];

        return new Set(
            indexes
                .map((index) => Number(index))
                .filter((index) => Number.isInteger(index) && index >= 0)
        );
    } catch (error) {
        console.error('Failed to parse no-bird data:', error);
        return new Set();
    }
};

const buildNoBirdsJson = (noBirdIndexes) => {
    const sortedIndexes = [...noBirdIndexes]
        .map((index) => Number(index))
        .filter((index) => Number.isInteger(index) && index >= 0)
        .sort((a, b) => a - b);

    return sortedIndexes.length > 0
        ? JSON.stringify({ indexes: sortedIndexes })
        : null;
};

const buildShotSequence = (scoreData) => {
    const pattern = Array.isArray(scoreData?.pattern) ? scoreData.pattern : [];
    const scores = Array.isArray(scoreData?.scores) ? scoreData.scores : [];

    if (!pattern.length) {
        return scores.map((score, index) => ({
            scoreIndex: index,
            station: 'General',
            pairType: 'single',
            shotLabel: `${index + 1}`,
            result: score
        }));
    }

    const flatShots = [];
    let scoreIndex = 0;

    pattern.forEach((patternEntry) => {
        const shots = Array.isArray(patternEntry.shots) ? patternEntry.shots : [];
        shots.forEach((shotLabel, shotOffset) => {
            flatShots.push({
                scoreIndex,
                station: patternEntry.station || 'General',
                pairType: patternEntry.type || 'single',
                shotLabel: shotLabel || `${scoreIndex + 1}`,
                pairSequence: patternEntry.sequence || scoreIndex + 1,
                shotOffset,
                result: scores[scoreIndex] ?? null
            });
            scoreIndex += 1;
        });
    });

    return flatShots;
};

const groupShotsByStation = (shotSequence) => {
    return shotSequence.reduce((groups, shot) => {
        if (!groups[shot.station]) {
            groups[shot.station] = [];
        }
        groups[shot.station].push(shot);
        return groups;
    }, {});
};

const normaliseStandConfig = (scoreSheet) => {
    if (!scoreSheet?.clayConfigJson) {
        return [];
    }

    try {
        const parsed = JSON.parse(scoreSheet.clayConfigJson);
        const standList = Array.isArray(parsed?.stands)
            ? parsed.stands
            : Array.isArray(parsed)
                ? parsed
                : [];

        return standList.map((stand, index) => ({
            standNumber: String(stand?.standNumber || stand?.number || stand?.stand || index + 1),
            standName: stand?.standName || stand?.name || stand?.label || '',
            targetLabels: Array.isArray(stand?.targetLabels)
                ? stand.targetLabels
                : Array.isArray(stand?.targets)
                    ? stand.targets.map((target, targetIndex) =>
                        typeof target === 'string'
                            ? target
                            : target?.label || target?.name || target?.targetLabel || `T${targetIndex + 1}`
                    )
                    : []
        }));
    } catch (error) {
        console.error('Failed to parse clay config for competitive scorer:', error);
        return [];
    }
};

const formatPairTypeLabel = (pairType) => {
    switch ((pairType || '').toLowerCase()) {
        case 'doubles':
            return 'Double Pair';
        case 'on-report':
            return 'On-Report Pair';
        case 'simultaneous':
            return 'Simultaneous Pair';
        case 'single':
            return 'Single';
        default:
            return pairType || 'Target';
    }
};

const formatShotLabel = (shotLabel, disciplineType) => {
    if (disciplineType === 'Skeet') {
        if (shotLabel === 'H') {
            return 'High House';
        }
        if (shotLabel === 'L') {
            return 'Low House';
        }
    }

    if (disciplineType === 'Trap' && /^T\d+$/.test(shotLabel || '')) {
        return `Trap ${shotLabel.slice(1)}`;
    }

    return shotLabel || 'Target';
};

const getDisciplineUnitLabel = (disciplineType) => {
    switch (disciplineType) {
        case 'Trap':
            return 'Peg';
        case 'Sporting':
            return 'Stand';
        case 'Skeet':
            return 'Station';
        default:
            return 'Station';
    }
};

const buildDisciplineFocus = (disciplineType, variant, currentShot, currentRoundStationSummary) => {
    const stationCount = currentRoundStationSummary.length || 0;
    const currentStationIndex = currentRoundStationSummary.findIndex((station) => station.isActive);
    const unitLabel = getDisciplineUnitLabel(disciplineType);
    const currentUnitNumber = currentShot?.station || currentRoundStationSummary[0]?.station || '-';

    let detail = 'Score the next expected shot in sequence.';

    if (currentShot) {
        const shotLabel = formatShotLabel(currentShot.shotLabel, disciplineType);
        const pairLabel = formatPairTypeLabel(currentShot.pairType);

        if (disciplineType === 'Skeet') {
            detail = `${shotLabel} ${pairLabel.toLowerCase()} on station ${currentShot.station}.`;
        } else if (disciplineType === 'Trap') {
            detail = `${pairLabel} from peg ${currentShot.station}${variant === 'DownTheLine' ? ' with DTL scoring' : ''}.`;
        } else if (disciplineType === 'Sporting') {
            detail = `${pairLabel} on stand ${currentShot.station} - ${shotLabel}.`;
        }
    }

    return {
        heading: `${unitLabel} ${currentUnitNumber}${stationCount > 0 && currentStationIndex >= 0 ? ` of ${stationCount}` : ''}`,
        detail,
        progress: `${currentRoundStationSummary.filter((station) => station.isComplete).length}/${stationCount} ${unitLabel.toLowerCase()}s complete`
    };
};

const buildStationHelperText = (disciplineType, shots) => {
    if (!Array.isArray(shots) || shots.length === 0) {
        return '';
    }

    const pairTypes = [...new Set(shots.map((shot) => formatPairTypeLabel(shot.pairType)))];
    const shotLabels = [...new Set(shots.map((shot) => formatShotLabel(shot.shotLabel, disciplineType)))];

    if (disciplineType === 'Skeet') {
        return shotLabels.join(' / ');
    }

    if (disciplineType === 'Trap') {
        return `${shots.length} targets${pairTypes.length > 0 ? ` - ${pairTypes.join(', ')}` : ''}`;
    }

    return pairTypes.join(', ') || shotLabels.join(', ');
};

const ScoreShoot = () => {
    const { scoreSheetId } = useParams();
    const navigate = useNavigate();
    const [scoreSheet, setScoreSheet] = useState(null);
    const [rounds, setRounds] = useState([]);
    const [selectedCase, setSelectedCase] = useState([0, 0]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSavingScore, setIsSavingScore] = useState(false);
    const [isFinishing, setIsFinishing] = useState(false);
    const [error, setError] = useState(null);
    const [hideCompletedStations, setHideCompletedStations] = useState(false);
    const [shooterOrder, setShooterOrder] = useState([]);

    const fetchScoreSheet = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await scoringService.getScoreSheet(scoreSheetId);
            setScoreSheet(data);
            setRounds(data.rounds || []);
        } catch (fetchError) {
            console.error('Error fetching score sheet:', fetchError);
            setError('Failed to load score sheet. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [scoreSheetId]);

    useEffect(() => {
        if (scoreSheetId) {
            fetchScoreSheet();
        }
    }, [scoreSheetId, fetchScoreSheet]);

    useEffect(() => {
        if (scoreSheet && (scoreSheet.scoreSheetType === 'FunShoot' || scoreSheet.variant === 'FunShoot')) {
            navigate(`/dashboard/score/fun-shoot/${scoreSheetId}`, { replace: true });
        }
    }, [navigate, scoreSheet, scoreSheetId]);

    const standConfig = useMemo(() => normaliseStandConfig(scoreSheet), [scoreSheet]);
    const standConfigMap = useMemo(
        () => Object.fromEntries(standConfig.map((stand) => [stand.standNumber, stand])),
        [standConfig]
    );

    const parsedRounds = useMemo(() => {
        return rounds.map((round) => {
            const parsedScoreData = parseScoreData(round.scoreData);
            const noBirdIndexes = parseNoBirdIndexes(round.noBirdsJson);
            const shotSequence = buildShotSequence(parsedScoreData).map((shot) => ({
                ...shot,
                isNoBird: noBirdIndexes.has(shot.scoreIndex)
            }));
            const groupedStations = groupShotsByStation(shotSequence);
            const completedShots = shotSequence.filter((shot) => shot.result === true || shot.result === false).length;
            const hits = shotSequence.filter((shot) => shot.result === true).length;
            const nextPendingIndex = shotSequence.findIndex((shot) => shot.result === null);

            return {
                ...round,
                parsedScoreData,
                shotSequence,
                groupedStations,
                completedShots,
                hits,
                noBirdIndexes,
                nextPendingIndex: nextPendingIndex >= 0 ? nextPendingIndex : 0
            };
        });
    }, [rounds]);

    useEffect(() => {
        if (!parsedRounds.length) {
            return;
        }

        const [roundIndex, shotIndex] = selectedCase;
        const safeRoundIndex = Math.min(roundIndex, parsedRounds.length - 1);
        const safeRound = parsedRounds[safeRoundIndex];
        const safeShotIndex = Math.min(shotIndex, Math.max(safeRound.shotSequence.length - 1, 0));

        if (safeRoundIndex !== roundIndex || safeShotIndex !== shotIndex) {
            setSelectedCase([safeRoundIndex, safeShotIndex]);
        }
    }, [parsedRounds, selectedCase]);

    const currentRound = parsedRounds[selectedCase[0]];
    const currentShot = currentRound?.shotSequence[selectedCase[1]] || null;
    const canManage = scoreSheet?.canManage !== false;
    const selectedShotIsNoBird = Boolean(currentShot?.isNoBird);
    const currentStandMeta = currentShot ? standConfigMap[String(currentShot.station)] : null;
    const overallHits = parsedRounds.reduce((sum, round) => sum + round.hits, 0);
    const overallShots = parsedRounds.reduce((sum, round) => sum + round.completedShots, 0);
    const totalSessionShots = parsedRounds.reduce((sum, round) => sum + round.shotSequence.length, 0);
    const currentShooterPercentage = currentRound?.completedShots
        ? ((currentRound.hits / currentRound.completedShots) * 100).toFixed(1)
        : '0.0';

    const currentRoundStationSummary = useMemo(() => {
        if (!currentRound?.groupedStations) {
            return [];
        }

        return Object.entries(currentRound.groupedStations).map(([station, shots]) => {
            const completed = shots.filter((shot) => shot.result !== null).length;
            const hits = shots.filter((shot) => shot.result === true).length;
            const isActive = station === String(currentShot?.station);
            const isComplete = shots.length > 0 && completed === shots.length;

            return {
                station,
                standName: standConfigMap[station]?.standName || `Stand ${station}`,
                helperText: buildStationHelperText(scoreSheet?.disciplineType, shots),
                completed,
                total: shots.length,
                hits,
                isActive,
                isComplete
            };
        });
    }, [currentRound, currentShot, standConfigMap, scoreSheet?.disciplineType]);

    const shooterRoundSummary = useMemo(() => {
        return parsedRounds.map((round, roundIndex) => {
            const pendingCount = round.shotSequence.filter((shot) => shot.result === null).length;
            const nextStation = pendingCount > 0
                ? round.shotSequence.find((shot) => shot.result === null)?.station
                : null;

            return {
                roundIndex,
                roundId: round.roundId,
                shooterName: round.shooterName || `Shooter ${roundIndex + 1}`,
                pendingCount,
                completedCount: round.completedShots,
                totalCount: round.shotSequence.length,
                hits: round.hits,
                nextStation,
                isComplete: pendingCount === 0 && round.shotSequence.length > 0
            };
        });
    }, [parsedRounds]);

    useEffect(() => {
        setShooterOrder((previousOrder) => {
            const availableRoundIds = shooterRoundSummary.map((round) => round.roundId);
            const retained = previousOrder.filter((roundId) => availableRoundIds.includes(roundId));
            const missing = availableRoundIds.filter((roundId) => !retained.includes(roundId));
            return [...retained, ...missing];
        });
    }, [shooterRoundSummary]);

    const orderedShooterRoundSummary = useMemo(() => {
        if (!shooterOrder.length) {
            return shooterRoundSummary;
        }

        const summaryByRoundId = new Map(shooterRoundSummary.map((round) => [round.roundId, round]));
        const ordered = shooterOrder
            .map((roundId) => summaryByRoundId.get(roundId))
            .filter(Boolean);

        if (ordered.length === shooterRoundSummary.length) {
            return ordered;
        }

        const existingIds = new Set(ordered.map((round) => round.roundId));
        const missing = shooterRoundSummary.filter((round) => !existingIds.has(round.roundId));
        return [...ordered, ...missing];
    }, [shooterOrder, shooterRoundSummary]);

    const completedShooterCount = shooterRoundSummary.filter((round) => round.isComplete).length;
    const nextIncompleteShooter = orderedShooterRoundSummary.find(
        (round) => round.roundIndex !== selectedCase[0] && !round.isComplete
    ) || orderedShooterRoundSummary.find((round) => !round.isComplete);
    const nextShotForCurrentShooter = currentRound?.shotSequence.find((shot) => shot.result === null) || null;
    const canFinishSession = parsedRounds.length > 0 && parsedRounds.every((round) =>
        round.shotSequence.length > 0 && round.shotSequence.every((shot) => shot.result !== null)
    );
    const disciplineFocus = useMemo(
        () => buildDisciplineFocus(scoreSheet?.disciplineType, scoreSheet?.variant, currentShot, currentRoundStationSummary),
        [scoreSheet?.disciplineType, scoreSheet?.variant, currentShot, currentRoundStationSummary]
    );

    const visibleStationEntries = useMemo(() => {
        const entries = Object.entries(currentRound?.groupedStations || {});
        if (!hideCompletedStations) {
            return entries;
        }

        return entries.filter(([, shots]) => shots.some((shot) => shot.result === null));
    }, [currentRound, hideCompletedStations]);

    const jumpToRoundPendingShot = useCallback((roundIndex) => {
        const nextRound = parsedRounds[roundIndex];
        if (!nextRound) {
            return;
        }

        const nextShotIndex = nextRound.shotSequence.findIndex((shot) => shot.result === null);
        setSelectedCase([roundIndex, nextShotIndex >= 0 ? nextShotIndex : nextRound.nextPendingIndex]);
    }, [parsedRounds]);

    const setSelectedRound = (roundIndex) => {
        jumpToRoundPendingShot(roundIndex);
    };

    const shuffleShooterOrder = () => {
        if (orderedShooterRoundSummary.length <= 1) {
            return;
        }

        const shuffled = [...orderedShooterRoundSummary];
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        setShooterOrder(shuffled.map((round) => round.roundId));
    };

    const moveToNextCase = useCallback(() => {
        if (!parsedRounds.length) {
            return;
        }

        const [roundIndex, shotIndex] = selectedCase;
        const round = parsedRounds[roundIndex];
        if (!round) {
            return;
        }

        const nextPendingInRound = round.shotSequence.findIndex(
            (shot, index) => index > shotIndex && shot.result === null
        );

        if (nextPendingInRound >= 0) {
            setSelectedCase([roundIndex, nextPendingInRound]);
            return;
        }

        const nextRoundIndex = parsedRounds.findIndex((candidateRound, index) =>
            index > roundIndex && candidateRound.shotSequence.some((shot) => shot.result === null)
        );

        if (nextRoundIndex >= 0) {
            setSelectedCase([nextRoundIndex, parsedRounds[nextRoundIndex].nextPendingIndex]);
            return;
        }

        const firstPendingRoundIndex = parsedRounds.findIndex((candidateRound) =>
            candidateRound.shotSequence.some((shot) => shot.result === null)
        );

        if (firstPendingRoundIndex >= 0) {
            setSelectedCase([firstPendingRoundIndex, parsedRounds[firstPendingRoundIndex].nextPendingIndex]);
        }
    }, [parsedRounds, selectedCase]);

    const moveToPreviousCase = useCallback(() => {
        if (!parsedRounds.length) {
            return;
        }

        const [roundIndex, shotIndex] = selectedCase;
        const round = parsedRounds[roundIndex];
        if (!round) {
            return;
        }

        if (shotIndex > 0) {
            setSelectedCase([roundIndex, shotIndex - 1]);
            return;
        }

        if (roundIndex > 0) {
            const previousRound = parsedRounds[roundIndex - 1];
            setSelectedCase([roundIndex - 1, Math.max(previousRound.shotSequence.length - 1, 0)]);
        }
    }, [parsedRounds, selectedCase]);

    const updateSelectedScore = async (result, options = {}) => {
        const { advanceAfterSave = true, noBirdIndexes: noBirdIndexesOverride = null, clearNoBird = false } = options;
        const [roundIndex, shotIndex] = selectedCase;
        const round = parsedRounds[roundIndex];
        if (!canManage || !round || isSavingScore) {
            return;
        }

        const updatedScoreData = {
            ...round.parsedScoreData,
            scores: [...(round.parsedScoreData.scores || [])]
        };
        updatedScoreData.scores[shotIndex] = result;

        const nextNoBirdIndexes = noBirdIndexesOverride
            ? new Set(noBirdIndexesOverride)
            : new Set(round.noBirdIndexes || []);

        if (clearNoBird || result !== null) {
            nextNoBirdIndexes.delete(shotIndex);
        }

        const updatedNoBirdsJson = buildNoBirdsJson(nextNoBirdIndexes);

        try {
            setIsSavingScore(true);
            await scoringService.updateRound(scoreSheetId, round.roundId, {
                scoreData: JSON.stringify(updatedScoreData),
                noBirdsJson: updatedNoBirdsJson
            });

            setRounds((currentRounds) =>
                currentRounds.map((existingRound, index) =>
                    index === roundIndex
                        ? {
                            ...existingRound,
                            scoreData: JSON.stringify(updatedScoreData),
                            noBirdsJson: updatedNoBirdsJson
                        }
                        : existingRound
                )
            );

            if (result !== null && advanceAfterSave) {
                const currentRoundStillHasPending = updatedScoreData.scores.some((score) => score === null);
                if (currentRoundStillHasPending) {
                    moveToNextCase();
                } else {
                    const nextIncompleteRoundIndex = parsedRounds.findIndex((candidateRound, candidateIndex) =>
                        candidateIndex !== roundIndex && candidateRound.shotSequence.some((shot) => shot.result === null)
                    );

                    if (nextIncompleteRoundIndex >= 0) {
                        jumpToRoundPendingShot(nextIncompleteRoundIndex);
                    }
                }
            }
        } catch (updateError) {
            console.error('Error updating round:', updateError);
            setError('Failed to update score. Please try again.');
        } finally {
            setIsSavingScore(false);
        }
    };

    const toggleNoBirdForSelectedShot = async () => {
        const [roundIndex, shotIndex] = selectedCase;
        const round = parsedRounds[roundIndex];
        if (!canManage || !round || !currentShot) {
            return;
        }

        const nextNoBirdIndexes = new Set(round.noBirdIndexes || []);
        if (nextNoBirdIndexes.has(shotIndex)) {
            nextNoBirdIndexes.delete(shotIndex);
        } else {
            nextNoBirdIndexes.add(shotIndex);
        }

        await updateSelectedScore(null, {
            advanceAfterSave: false,
            noBirdIndexes: nextNoBirdIndexes
        });
    };

    const handleShotTileClick = async (shot) => {
        if (!canManage || !shot) {
            return;
        }

        const isCurrentlySelected =
            selectedCase[0] === parsedRounds.indexOf(currentRound) &&
            selectedCase[1] === shot.scoreIndex;

        setSelectedCase([selectedCase[0], shot.scoreIndex]);

        if (!isCurrentlySelected) {
            return;
        }

        const nextResult = shot.result === null
            ? true
            : shot.result === true
                ? false
                : null;

        await updateSelectedScore(nextResult, { advanceAfterSave: false });
    };

    const finishScoring = async () => {
        if (!canManage) {
            return;
        }

        setError(null);

        try {
            setIsFinishing(true);
            await scoringService.finishScoreSheet(scoreSheetId);

            navigate(`/dashboard/score/summary/${scoreSheetId}`);
        } catch (finishError) {
            console.error('Error finalising clay score sheet:', finishError);
            setError('Failed to finalise this score sheet. Please try again.');
        } finally {
            setIsFinishing(false);
        }
    };

    if (isLoading) {
        return <div className="loading">Loading...</div>;
    }

    if (error) {
        return <div className="error">Error: {error}</div>;
    }

    if (!scoreSheet || !parsedRounds.length) {
        return <div className="error">No score sheet or rounds found.</div>;
    }

    return (
        <div className="dashboard-component score-shoot">
            <div className="dashboard-component-headline">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <img src={backIcon} alt="Back" />
                    Back to Score Sheets
                </button>
                <h1 className="dashboard-component-headline-title">{scoreSheet.eventName || 'Unnamed Event'}</h1>
                <h2>{scoreSheet.disciplineType || 'Unknown Discipline'} - {scoreSheet.variant || 'Unknown Variant'}</h2>
            </div>

            <div className="score-summary-card">
                <div className="score-summary-content">
                    <div className="score-summary-item">
                        <span className="score-label">Session</span>
                        <span className="score-value score-value-small">{overallHits}/{overallShots}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Shooters Complete</span>
                        <span className="score-value score-value-small">{completedShooterCount}/{parsedRounds.length}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Shooter</span>
                        <span className="score-value score-value-small">{currentRound?.shooterName || 'Shooter'}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Next</span>
                        <span className="score-value score-value-small">
                            {currentShot
                                ? `${currentStandMeta?.standName || `Stand ${currentShot.station}`} ${currentShot.shotLabel}`
                                : 'Complete'}
                        </span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Shooter Score</span>
                        <span className="score-value">{currentRound?.hits || 0}/{currentRound?.completedShots || 0}</span>
                    </div>
                    <div className="score-summary-item">
                        <span className="score-label">Overall</span>
                        <span className="score-value">{overallShots}/{totalSessionShots}</span>
                    </div>
                </div>
            </div>

            <div className="instructions-card">
                {canManage ? (
                    <>
                        <p>Score the current target, then the next expected shot is brought into focus.</p>
                        <p>Use the shooter tabs to swap cards quickly. Tap a shot tile once to select it, then tap again to cycle hit, miss, and pending.</p>
                    </>
                ) : (
                    <>
                        <p>You can view this score sheet because you were added as a shooter.</p>
                        <p>Only the event owner or Shoot Connect staff can record shots or finalise scoring.</p>
                    </>
                )}
            </div>

            <div className="competitive-discipline-banner">
                <div>
                    <span className="score-label">{scoreSheet.disciplineType} Flow</span>
                    <h3>{disciplineFocus.heading}</h3>
                    <p>{disciplineFocus.detail}</p>
                </div>
                <div className="competitive-discipline-progress">
                    <strong>{disciplineFocus.progress}</strong>
                    <small>{scoreSheet.variant}</small>
                </div>
            </div>

            <div className="competitive-focus-bar">
                <label className="competitive-focus-toggle">
                    <input
                        type="checkbox"
                        checked={hideCompletedStations}
                        onChange={(e) => setHideCompletedStations(e.target.checked)}
                    />
                    <span>Hide completed stands</span>
                </label>
                <div className="fun-shoot-toolbar-actions">
                    {canManage && orderedShooterRoundSummary.length > 1 && (
                        <button
                            type="button"
                            className="competitive-focus-btn"
                            onClick={shuffleShooterOrder}
                        >
                            Shuffle Shooters
                        </button>
                    )}
                    {nextIncompleteShooter && (
                        <button
                            type="button"
                            className="competitive-focus-btn"
                            onClick={() => jumpToRoundPendingShot(nextIncompleteShooter.roundIndex)}
                        >
                            Jump To Next Incomplete Shooter
                        </button>
                    )}
                </div>
            </div>

            {nextShotForCurrentShooter && (
                <div className="competitive-next-target-bar">
                    <div>
                        <strong>Next For {currentRound?.shooterName}</strong>
                        <p>
                            {standConfigMap[String(nextShotForCurrentShooter.station)]?.standName || `${getDisciplineUnitLabel(scoreSheet?.disciplineType)} ${nextShotForCurrentShooter.station}`} - {formatShotLabel(nextShotForCurrentShooter.shotLabel, scoreSheet?.disciplineType)}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="competitive-focus-btn"
                        onClick={() => setSelectedCase([selectedCase[0], nextShotForCurrentShooter.scoreIndex])}
                    >
                        Go To Next Shot
                    </button>
                </div>
            )}

            <div className="fun-shoot-shooter-tabs competitive-shooter-tabs">
                {orderedShooterRoundSummary.map((round) => (
                    <button
                        key={round.roundId}
                        type="button"
                        className={`fun-shoot-shooter-tab ${selectedCase[0] === round.roundIndex ? 'active' : ''} ${round.isComplete ? 'complete' : ''}`}
                        onClick={() => setSelectedRound(round.roundIndex)}
                    >
                        <span>{round.shooterName}</span>
                        <small>
                            {round.isComplete
                                ? `${round.hits}/${round.completedCount} complete`
                                : `${round.pendingCount} pending${round.nextStation ? ` - next ${standConfigMap[String(round.nextStation)]?.standName || `stand ${round.nextStation}`}` : ''}`}
                        </small>
                    </button>
                ))}
            </div>

            <div className="competitive-current-shot-card">
                <div>
                    <span className="score-label">Current Station</span>
                    <h3>{currentShot ? (currentStandMeta?.standName || `${getDisciplineUnitLabel(scoreSheet?.disciplineType)} ${currentShot.station}`) : 'All Complete'}</h3>
                    <p>
                        {currentShot
                            ? `${formatPairTypeLabel(currentShot.pairType)} - ${formatShotLabel(currentShot.shotLabel, scoreSheet?.disciplineType)}${currentStandMeta?.standName ? ` on ${getDisciplineUnitLabel(scoreSheet?.disciplineType).toLowerCase()} ${currentShot.station}` : ''}`
                            : 'Every recorded shot for this shooter has been completed.'}
                    </p>
                </div>
                <div className="competitive-current-shot-stats">
                    <strong>{currentRound?.hits || 0}/{currentRound?.completedShots || 0}</strong>
                    <small>{currentShooterPercentage}% hit rate</small>
                </div>
            </div>

            <div className="competitive-station-progress">
                {currentRoundStationSummary.map((station) => (
                    <button
                        key={station.station}
                        type="button"
                        className={`competitive-station-pill ${station.isActive ? 'active' : ''} ${station.isComplete ? 'complete' : ''}`}
                        onClick={() => {
                            const firstShotInStation = currentRound?.shotSequence.findIndex(
                                (shot) => String(shot.station) === station.station
                            );
                            if (firstShotInStation >= 0) {
                                setSelectedCase([selectedCase[0], firstShotInStation]);
                            }
                        }}
                    >
                        <span>{station.standName}</span>
                        {station.helperText && <small>{station.helperText}</small>}
                        <small>{station.hits}/{station.completed}/{station.total}</small>
                    </button>
                ))}
            </div>

            <div className="competitive-nav-bar">
                <button
                    type="button"
                    className="competitive-nav-btn"
                    onClick={moveToPreviousCase}
                    disabled={isSavingScore || (selectedCase[0] === 0 && selectedCase[1] === 0)}
                >
                    Previous Shot
                </button>
                {canManage && (
                    <>
                        <button
                            type="button"
                            className="competitive-nav-btn competitive-nav-clear"
                            onClick={() => updateSelectedScore(null, { advanceAfterSave: false, clearNoBird: true })}
                            disabled={isSavingScore || !currentShot || (currentShot.result === null && !selectedShotIsNoBird)}
                        >
                            Clear Shot
                        </button>
                        <button
                            type="button"
                            className="competitive-nav-btn competitive-nav-no-bird"
                            onClick={toggleNoBirdForSelectedShot}
                            disabled={isSavingScore || !currentShot}
                        >
                            {selectedShotIsNoBird ? 'Clear No Bird' : 'Mark No Bird'}
                        </button>
                    </>
                )}
                <button
                    type="button"
                    className="competitive-nav-btn"
                    onClick={moveToNextCase}
                    disabled={isSavingScore || !currentShot}
                >
                    Next Shot
                </button>
            </div>

            {canManage && (
                <div className="scoring-btns">
                    <button className="scoring-miss-btn" onClick={() => updateSelectedScore(false)} disabled={isSavingScore || !currentShot}>
                        <img src={missIcon} alt="Miss" />
                        Miss
                    </button>
                    <button className="scoring-hit-btn" onClick={() => updateSelectedScore(true)} disabled={isSavingScore || !currentShot}>
                        <img src={hitIcon} alt="Hit" />
                        Hit
                    </button>
                </div>
            )}

            <div className="competitive-stations">
                {visibleStationEntries.map(([station, shots]) => (
                    <div key={station} className="competitive-station-card">
                        <div className="competitive-station-header">
                            <div>
                                <h3>{standConfigMap[station]?.standName || `${getDisciplineUnitLabel(scoreSheet?.disciplineType)} ${station}`}</h3>
                                {standConfigMap[station]?.standName && (
                                    <small className="competitive-station-subtitle">{getDisciplineUnitLabel(scoreSheet?.disciplineType)} {station}</small>
                                )}
                                <small className="competitive-station-helper">{buildStationHelperText(scoreSheet?.disciplineType, shots)}</small>
                            </div>
                            <span>
                                {shots.filter((shot) => shot.result === true).length}/
                                {shots.filter((shot) => shot.result !== null).length}
                            </span>
                        </div>
                        <div className="competitive-shot-grid">
                            {shots.map((shot) => (
                                <button
                                    key={`${station}-${shot.scoreIndex}`}
                                    type="button"
                                    className={`competitive-shot-cell ${
                                        selectedCase[1] === shot.scoreIndex ? 'selected' : ''
                                    } ${shot.result === true ? 'hit' : ''} ${shot.result === false ? 'miss' : ''} ${shot.isNoBird ? 'no-bird' : ''}`}
                                    onClick={() => handleShotTileClick(shot)}
                                    disabled={isSavingScore || !canManage}
                                >
                                    <span className="competitive-shot-label">{formatShotLabel(shot.shotLabel, scoreSheet?.disciplineType)}</span>
                                    <small>{formatPairTypeLabel(shot.pairType)}</small>
                                    <strong className="competitive-shot-result">
                                        {shot.result === true
                                            ? 'Hit'
                                            : shot.result === false
                                                ? 'Miss'
                                                : shot.isNoBird
                                                    ? 'No Bird'
                                                    : 'Pending'}
                                    </strong>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
                {visibleStationEntries.length === 0 && (
                    <div className="competitive-empty-state">
                        <h3>All visible stands complete</h3>
                        <p>This shooter has no incomplete stands left in the current filter.</p>
                    </div>
                )}
            </div>

            {canManage && (
                <div className="finish-container">
                    <button
                        type="button"
                        className="finish-button"
                        onClick={finishScoring}
                        disabled={!canFinishSession || isFinishing}
                    >
                        {isFinishing ? 'Finalising...' : 'Finish And View Summary'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ScoreShoot;
